import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/contexts/ToastContext";
import {
  DOCUMENTS,
  getEntriesByDocument,
  getEntryById,
  type Document,
} from "@/data/updateStatusCatalog";
import { api } from "@/lib/api";
import { API_PATHS } from "@/lib/api-paths";
import { API_BASE_URL } from "@/lib/config";
import { cn } from "@/lib/utils";
import type {
  LinerWebhookSimulationConfig,
  SimulationConfig,
  SimulateErrorApiKey,
} from "@/types/api";
import { DCSA_SIMULATE_ERROR_KEYS, SIMULATE_ERROR_KEYS_ORDER } from "@/types/api";

const DEFAULT_LINER_WEBHOOK: LinerWebhookSimulationConfig = {
  autoAfterAmend: false,
  autoAfterCancel: false,
  randomRejection: false,
};
import {
  applyLinerMergeFields,
  getLinerWebhookPreset,
  LINER_WEBHOOK_PRESETS,
} from "@/types/linerWebhookPresets";
import {
  sampleCancel,
  sampleCalculate,
  sampleDcsaBooking,
  sampleExportDecl,
  sampleGCL,
  sampleGetXml,
  samplePrintDocument,
  sampleSendExportDecl,
  sampleSendMockWebhook,
  sampleShort,
} from "@/types/api";
import { useState, useCallback, useMemo, useEffect } from "react";
import type { LucideIcon } from "lucide-react";
import {
  Ban,
  Check,
  ClipboardList,
  CodeXml,
  Copy,
  FileOutput,
  FileText,
  HeartPulse,
  LogIn,
  Ship,
  SlidersHorizontal,
  Webhook,
} from "lucide-react";

type EntryMeta = {
  ReferenceNumber: string;
  InterfaceId?: string;
  OMSCustomsId?: string;
};
function ExistingEntryPicker({
  listPath,
  token,
  onSelect,
}: {
  listPath: string;
  token: string | null;
  onSelect: (entry: EntryMeta) => void;
}) {
  const [entries, setEntries] = useState<EntryMeta[]>([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState<string>("");

  const fetchEntries = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    const res = await api.get<{ Success: boolean; Data: EntryMeta[] }>(
      `${listPath}?limit=50`,
      { token }
    );
    setLoading(false);
    if (res.ok && res.data && Array.isArray((res.data as any).Data)) {
      setEntries((res.data as any).Data as EntryMeta[]);
    }
  }, [listPath, token]);

  const handleSelect = useCallback(
    (refNum: string) => {
      setSelected(refNum);
      const entry = entries.find((e) => e.ReferenceNumber === refNum);
      if (entry) onSelect(entry);
    },
    [entries, onSelect]
  );

  return (
    <div className="flex flex-wrap items-end gap-2">
      <div className="flex-1 min-w-[180px] space-y-1">
        <Label className="text-muted-foreground text-xs">Load existing entry</Label>
        <Select value={selected} onValueChange={handleSelect} disabled={entries.length === 0}>
          <SelectTrigger className="font-mono text-sm">
            <SelectValue placeholder={entries.length === 0 ? "Fetch first…" : "Select entry"} />
          </SelectTrigger>
          <SelectContent>
            {entries.map((e) => (
              <SelectItem key={e.ReferenceNumber} value={e.ReferenceNumber}>
                <span className="font-mono">{e.ReferenceNumber}</span>
                {e.OMSCustomsId && (
                  <span className="ml-2 text-muted-foreground text-xs">({e.OMSCustomsId})</span>
                )}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={fetchEntries}
        disabled={loading || !token}
      >
        {loading ? "Loading…" : "Fetch"}
      </Button>
    </div>
  );
}

type TabId =
  | "login"
  | "health"
  | "export"
  | "gcl"
  | "short"
  | "cancel"
  | "xml"
  | "webhooks"
  | "liner"
  | "config";

type NavTab = { id: TabId; label: string; icon: LucideIcon };

const NAV_GROUPS: { title: string; tabs: NavTab[] }[] = [
  {
    title: "General",
    tabs: [
      { id: "login", label: "Login", icon: LogIn },
      { id: "health", label: "Health", icon: HeartPulse },
      { id: "config", label: "Config", icon: SlidersHorizontal },
    ],
  },
  {
    title: "Customs",
    tabs: [
      { id: "export", label: "Export", icon: FileOutput },
      { id: "gcl", label: "GCL", icon: ClipboardList },
      { id: "short", label: "Short", icon: FileText },
      { id: "cancel", label: "Cancel", icon: Ban },
      { id: "xml", label: "XML", icon: CodeXml },
      { id: "webhooks", label: "Webhooks", icon: Webhook },
    ],
  },
  {
    title: "Liner",
    tabs: [{ id: "liner", label: "Liner", icon: Ship }],
  },
];

const TABS: { id: TabId; label: string }[] = NAV_GROUPS.flatMap((g) => g.tabs);

const TAB_IDS = new Set<string>(TABS.map((t) => t.id));

function getTabFromSearch(): TabId {
  const params = new URLSearchParams(typeof window !== "undefined" ? window.location.search : "");
  const t = params.get("tab");
  return (t && TAB_IDS.has(t) ? t : "login") as TabId;
}

function setTabInUrl(tab: TabId) {
  const url = new URL(window.location.href);
  url.searchParams.set("tab", tab);
  window.history.replaceState({ tab }, "", url.pathname + url.search);
}

function ResponseBlock({
  status,
  data,
  ok,
}: {
  status: number;
  data: unknown;
  ok: boolean;
}) {
  const [copied, setCopied] = useState(false);
  let text: string;
  if (data instanceof Blob) {
    text = `[Blob ${data.size} bytes]`;
  } else {
    text = typeof data === "string" ? data : JSON.stringify(data, null, 2);
  }
  const copyToClipboard = useCallback(() => {
    void navigator.clipboard.writeText(`HTTP ${status}\n${text}`).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }, [status, text]);

  return (
    <div className="mt-4 relative">
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className="absolute top-2 right-2 z-10 h-8 w-8 text-muted-foreground hover:text-foreground"
        onClick={copyToClipboard}
        aria-label={copied ? "Copied" : "Copy response"}
      >
        {copied ? <Check className="size-4 text-green-600" /> : <Copy className="size-4" />}
      </Button>
      <pre
        className={cn(
          "w-full overflow-auto rounded-lg border p-4 pr-12 text-left text-sm font-mono shadow-sm transition-all scrollbar-custom",
          ok ? "border-green-500/20 bg-green-500/5 text-green-700 dark:text-green-400" : "border-destructive/30 bg-destructive/10 text-destructive-foreground dark:text-destructive"
        )}
      >
        <span className="font-semibold opacity-70">HTTP {status}</span>
        {"\n"}
        {text}
      </pre>
    </div>
  );
}

function validateJsonBody(body: string): boolean {
  const trimmed = body.trim();
  if (trimmed === "" || trimmed === "null") return true;
  try {
    JSON.parse(body);
    return true;
  } catch {
    return false;
  }
}

function EndpointBlock({
  title,
  loading,
  response,
  onSend,
  bodyLabel = "Request body (JSON)",
  defaultBody,
  tokenRequired = true,
  listPath,
  children,
  className,
}: {
  title: string;
  loading: boolean;
  response: { status: number; data: unknown; ok: boolean } | null;
  onSend: (body: string) => void;
  bodyLabel?: string;
  defaultBody: string;
  tokenRequired?: boolean;
  /** If provided, shows an existing-entry picker that injects InterfaceId, OMSCustomsId, ReferenceNumber into the body */
  listPath?: string;
  children?: React.ReactNode;
  className?: string;
}) {
  const { token } = useAuth();
  const [body, setBody] = useState(defaultBody);
  const [jsonError, setJsonError] = useState<string | null>(null);
  const disabled = tokenRequired && !token;

  const injectEntry = useCallback(
    (entry: EntryMeta) => {
      setBody((prev) => {
        let next = prev;
        if (entry.InterfaceId !== undefined) {
          const escaped = entry.InterfaceId.replace(/\\/g, "\\\\").replace(/"/g, '\\"');
          if (/"InterfaceId"\s*:/.test(next)) {
            next = next.replace(/"InterfaceId"\s*:\s*"[^"]*"/, `"InterfaceId": "${escaped}"`);
          }
        }
        if (entry.OMSCustomsId !== undefined) {
          const escaped = entry.OMSCustomsId.replace(/\\/g, "\\\\").replace(/"/g, '\\"');
          next = next.replace(
            /"OMSCustomsId"\s*:\s*(?:null|"[^"]*")/,
            `"OMSCustomsId": "${escaped}"`
          );
        }
        const refEscaped = entry.ReferenceNumber.replace(/\\/g, "\\\\").replace(/"/g, '\\"');
        next = next.replace(
          /"ReferenceNumber"\s*:\s*"[^"]*"/,
          `"ReferenceNumber": "${refEscaped}"`
        );
        return next;
      });
    },
    []
  );

  return (
    <Card
      className={cn(
        "flex min-h-0 flex-col border-white/10 bg-card/50 shadow-sm backdrop-blur-sm transition-all hover:shadow-md",
        "mt-4",
        className
      )}
    >
      <CardHeader className="shrink-0 pb-2">
        <CardTitle className="text-lg font-semibold tracking-tight text-primary">{title}</CardTitle>
      </CardHeader>
      <CardContent className="flex min-h-0 flex-1 flex-col space-y-4 overflow-hidden">
        {children}
        {listPath && (
          <ExistingEntryPicker
            listPath={listPath}
            token={token}
            onSelect={injectEntry}
          />
        )}
        <div className="flex min-h-0 flex-1 flex-col space-y-1.5">
          <Label className="shrink-0 text-xs font-medium uppercase tracking-wider text-muted-foreground">
            {bodyLabel}
          </Label>
          <textarea
            value={body}
            onChange={(e) => {
              setBody(e.target.value);
              if (jsonError) setJsonError(null);
            }}
            onBlur={() => {
              setJsonError(
                body.trim() !== "" && body.trim() !== "null" && !validateJsonBody(body)
                  ? "Invalid JSON"
                  : null
              );
            }}
            className={cn(
              "mt-1 min-h-[200px] w-full flex-1 resize-y rounded-lg border border-input/50 bg-background/50 px-4 py-3 font-mono text-sm scrollbar-custom sm:min-h-[280px] lg:min-h-[320px]",
              "focus:border-primary/50 focus:outline-none focus:ring-2 focus:ring-primary/40 transition-all",
              jsonError && "border-destructive/50"
            )}
            spellCheck={false}
          />
          {jsonError && (
            <p className="shrink-0 text-sm font-medium text-destructive" role="alert">
              {jsonError}
            </p>
          )}
        </div>
        <div className="flex shrink-0 items-center gap-4 pt-2">
          <Button
            onClick={() => {
              if (body.trim() !== "" && body.trim() !== "null" && !validateJsonBody(body)) {
                setJsonError("Invalid JSON");
                return;
              }
              setJsonError(null);
              onSend(body);
            }}
            disabled={disabled || loading}
            className="w-32 bg-primary/90 hover:bg-primary shadow-sm shadow-primary/20 transition-all"
          >
            {loading ? "Sending…" : "Send Request"}
          </Button>
          {disabled && tokenRequired && (
            <div
              className="rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-sm font-medium text-amber-800 dark:text-amber-200"
              role="alert"
            >
              🔒 Login required
            </div>
          )}
        </div>
        {response && (
          <div className="max-h-[min(40vh,24rem)] shrink-0 animate-in fade-in slide-in-from-bottom-2 overflow-y-auto duration-300 scrollbar-custom">
            <ResponseBlock {...response} />
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export function SimulationUI() {
  const { token, login, logout, isAuthenticated } = useAuth();
  const { toast } = useToast();
  const [tab, setTabState] = useState<TabId>(getTabFromSearch);

  const setTab = useCallback((next: TabId) => {
    setTabState(next);
    setTabInUrl(next);
  }, []);

  useEffect(() => {
    const onPopState = () => setTabState(getTabFromSearch());
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, []);
  const [loginUsername, setLoginUsername] = useState("admin");
  const [loginPassword, setLoginPassword] = useState("admin");
  const [loginError, setLoginError] = useState<string | null>(null);
  const [loginLoading, setLoginLoading] = useState(false);
  const [healthResp, setHealthResp] = useState<{
    status: number;
    data: unknown;
    ok: boolean;
  } | null>(null);
  const [healthLoading, setHealthLoading] = useState(false);
  const [exportResp, setExportResp] = useState<{
    status: number;
    data: unknown;
    ok: boolean;
  } | null>(null);
  const [exportLoading, setExportLoading] = useState(false);
  const [gclResp, setGclResp] = useState<{
    status: number;
    data: unknown;
    ok: boolean;
  } | null>(null);
  const [gclLoading, setGclLoading] = useState(false);
  const [shortResp, setShortResp] = useState<{
    status: number;
    data: unknown;
    ok: boolean;
  } | null>(null);
  const [shortLoading, setShortLoading] = useState(false);
  const [cancelResp, setCancelResp] = useState<{
    status: number;
    data: unknown;
    ok: boolean;
  } | null>(null);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [xmlResp, setXmlResp] = useState<{
    status: number;
    data: unknown;
    ok: boolean;
  } | null>(null);
  const [xmlLoading, setXmlLoading] = useState(false);
  const [webhooksResp, setWebhooksResp] = useState<{
    status: number;
    data: unknown;
    ok: boolean;
  } | null>(null);
  const [webhooksLoading, setWebhooksLoading] = useState(false);
  const [linerResp, setLinerResp] = useState<{
    status: number;
    data: unknown;
    ok: boolean;
  } | null>(null);
  const [linerLoading, setLinerLoading] = useState(false);
  const [linerApiKey, setLinerApiKey] = useState("");
  /** Shown in UI; sent as X-Liner-Webhook-Client-Key once defaults have loaded. */
  const [linerOutboundWebhookKey, setLinerOutboundWebhookKey] = useState("");
  /** Until false, omit X-Liner-Webhook-Client-Key while saved defaults are loading. */
  const [linerSkipOutboundClientHeader, setLinerSkipOutboundClientHeader] =
    useState(true);

  const defaultLinerPreset = LINER_WEBHOOK_PRESETS[0];
  const [linerWebhookPresetId, setLinerWebhookPresetId] = useState(
    defaultLinerPreset?.id ?? "maersk_booking_v2_initial_confirmed"
  );
  const [linerWHLsaRef, setLinerWHLsaRef] = useState(
    defaultLinerPreset?.defaults.lsaBookingRef ?? ""
  );
  const [linerWHLinerCode, setLinerWHLinerCode] = useState(
    defaultLinerPreset?.defaults.linerCode ?? ""
  );
  const [linerWHCloudEventId, setLinerWHCloudEventId] = useState<string>(() => crypto.randomUUID());
  const [linerWHSubject, setLinerWHSubject] = useState(
    defaultLinerPreset?.defaults.subject ?? ""
  );
  const [linerWHTime, setLinerWHTime] = useState(() => new Date().toISOString());
  const [linerWebhookBody, setLinerWebhookBody] = useState("");
  const [linerWebhookJsonError, setLinerWebhookJsonError] = useState<string | null>(null);
  const [linerWebhookLoading, setLinerWebhookLoading] = useState(false);
  const [linerWebhookResp, setLinerWebhookResp] = useState<{
    status: number;
    data: unknown;
    ok: boolean;
  } | null>(null);
  const [linerWebhookUrlFromServer, setLinerWebhookUrlFromServer] = useState("");

  const fetchLinerWebhookDefaults = useCallback(async () => {
    const res = await api.get<{
      inboundApiKey?: string;
      outboundWebhookApiKey?: string;
      linerWebhookUrl?: string;
    }>(API_PATHS.LINER_WEBHOOKS_DEFAULTS);
    if (!res.ok || !res.data || typeof res.data !== "object") {
      return;
    }
    const d = res.data as Record<string, unknown>;
    if (typeof d.inboundApiKey === "string") {
      setLinerApiKey(d.inboundApiKey);
    }
    if (typeof d.outboundWebhookApiKey === "string") {
      setLinerOutboundWebhookKey(d.outboundWebhookApiKey);
      setLinerSkipOutboundClientHeader(false);
    }
    if (typeof d.linerWebhookUrl === "string") {
      setLinerWebhookUrlFromServer(d.linerWebhookUrl);
    } else {
      setLinerWebhookUrlFromServer("");
    }
  }, []);

  useEffect(() => {
    void fetchLinerWebhookDefaults();
  }, [fetchLinerWebhookDefaults]);

  useEffect(() => {
    const p = getLinerWebhookPreset(linerWebhookPresetId);
    if (!p) return;
    setLinerWHLsaRef(p.defaults.lsaBookingRef);
    setLinerWHLinerCode(p.defaults.linerCode);
    setLinerWHSubject(p.defaults.subject);
    setLinerWHCloudEventId(crypto.randomUUID());
    setLinerWHTime(new Date().toISOString());
  }, [linerWebhookPresetId]);

  useEffect(() => {
    const p = getLinerWebhookPreset(linerWebhookPresetId);
    if (!p) return;
    const merged = applyLinerMergeFields(structuredClone(p.payload), {
      lsaBookingRef: linerWHLsaRef,
      linerCode: linerWHLinerCode,
      cloudEventId: linerWHCloudEventId,
      subject: linerWHSubject,
      isoTime: linerWHTime,
    });
    setLinerWebhookBody(JSON.stringify(merged, null, 2));
  }, [
    linerWebhookPresetId,
    linerWHLsaRef,
    linerWHLinerCode,
    linerWHCloudEventId,
    linerWHSubject,
    linerWHTime,
  ]);

  // Simulation config state
  const [simConfig, setSimConfig] = useState<SimulationConfig | null>(null);
  const [simConfigLoading, setSimConfigLoading] = useState(false);
  const [simConfigSaving, setSimConfigSaving] = useState(false);
  const [simConfigError, setSimConfigError] = useState<string | null>(null);

  const [selectedDocument, setSelectedDocument] = useState<Document>("Export");
  const webhookEntries = useMemo(
    () => getEntriesByDocument(selectedDocument),
    [selectedDocument]
  );
  const [selectedMessageId, setSelectedMessageId] = useState<string | null>(() => {
    const first = getEntriesByDocument("Export")[0];
    return first?.id ?? null;
  });
  const selectedEntry = selectedMessageId ? getEntryById(selectedMessageId) : null;
  const onDocumentChange = useCallback((doc: Document) => {
    setSelectedDocument(doc);
    const entries = getEntriesByDocument(doc);
    setSelectedMessageId(entries[0]?.id ?? null);
  }, []);

  const [webhookBody, setWebhookBody] = useState(() => {
    const e = getEntriesByDocument("Export")[0];
    const full = e ? getEntryById(e.id) : null;
    return full ? JSON.stringify(full.payload, null, 2) : JSON.stringify(sampleSendMockWebhook, null, 2);
  });
  const [webhookInterfaceId, setWebhookInterfaceId] = useState(() => {
    const e = getEntriesByDocument("Export")[0];
    const full = e ? getEntryById(e.id) : null;
    const p = full?.payload as Record<string, unknown> | undefined;
    return p ? String(p.InterfaceId ?? "") : "";
  });
  const [webhookReferenceNumber, setWebhookReferenceNumber] = useState(() => {
    const e = getEntriesByDocument("Export")[0];
    const full = e ? getEntryById(e.id) : null;
    const p = full?.payload as Record<string, unknown> | undefined;
    return p ? String(p.ReferenceNumber ?? "") : "";
  });
  const [webhookOMSCustomsId, setWebhookOMSCustomsId] = useState(() => {
    const e = getEntriesByDocument("Export")[0];
    const full = e ? getEntryById(e.id) : null;
    const p = full?.payload as Record<string, unknown> | undefined;
    return p ? String(p.OMSCustomsId ?? "") : "";
  });
  const [webhookJsonError, setWebhookJsonError] = useState<string | null>(null);

  useEffect(() => {
    if (!selectedEntry) return;
    const template = selectedEntry.payload as Record<string, unknown>;
    const merged: Record<string, unknown> = { ...template };
    const keptInterfaceId = webhookInterfaceId.trim();
    const keptReferenceNumber = webhookReferenceNumber.trim();
    const keptOMSCustomsId = webhookOMSCustomsId.trim();
    merged.InterfaceId = keptInterfaceId || template.InterfaceId;
    merged.ReferenceNumber = keptReferenceNumber || template.ReferenceNumber;
    merged.OMSCustomsId = keptOMSCustomsId || null;
    setWebhookBody(JSON.stringify(merged, null, 2));
    setWebhookInterfaceId(String(merged.InterfaceId ?? ""));
    setWebhookReferenceNumber(String(merged.ReferenceNumber ?? ""));
    setWebhookOMSCustomsId(merged.OMSCustomsId != null ? String(merged.OMSCustomsId) : "");
  }, [selectedMessageId, selectedEntry]);

  const escapeJsonString = useCallback((value: string) => value.replace(/\\/g, "\\\\").replace(/"/g, '\\"'), []);
  const injectInterfaceId = useCallback(
    (value: string) => {
      setWebhookInterfaceId(value);
      const escaped = escapeJsonString(value);
      setWebhookBody((prev) =>
        prev.replace(/"InterfaceId"\s*:\s*"[^"]*"/, `"InterfaceId": "${escaped}"`)
      );
    },
    [escapeJsonString]
  );
  const injectReferenceNumber = useCallback(
    (value: string) => {
      setWebhookReferenceNumber(value);
      const escaped = escapeJsonString(value);
      setWebhookBody((prev) =>
        prev.replace(/"ReferenceNumber"\s*:\s*"[^"]*"/, `"ReferenceNumber": "${escaped}"`)
      );
    },
    [escapeJsonString]
  );
  const injectOMSCustomsId = useCallback((value: string) => {
    setWebhookOMSCustomsId(value);
    setWebhookBody((prev) => {
      const re = /"OMSCustomsId"\s*:\s*(?:null|"[^"]*")/;
      const replacement = value === "" ? '"OMSCustomsId": null' : `"OMSCustomsId": "${escapeJsonString(value)}"`;
      return prev.replace(re, replacement);
    });
  }, [escapeJsonString]);

  const handleLogin = useCallback(async () => {
    setLoginError(null);
    setLoginLoading(true);
    const result = await login(loginUsername, loginPassword);
    setLoginLoading(false);
    if (result.ok) {
      toast("Logged in successfully", "success");
    } else {
      const msg = result.error ?? "Login failed";
      setLoginError(msg);
      toast(msg, "error");
    }
  }, [login, loginUsername, loginPassword, toast]);

  const handleHealth = useCallback(async () => {
    setHealthLoading(true);
    const res = await api.get(API_PATHS.HEALTH);
    setHealthResp(res);
    setHealthLoading(false);
  }, []);

  const parseJson = useCallback((s: string): unknown => {
    try {
      return JSON.parse(s);
    } catch {
      return null;
    }
  }, []);

  const handleExportSend = useCallback(
    async (body: string) => {
      const parsed = parseJson(body);
      if (!parsed) {
        setExportResp({ status: 0, data: "Invalid JSON", ok: false });
        return;
      }
      setExportLoading(true);
      const res = await api.post(API_PATHS.EXPORT_DECL, {
        body: parsed,
        token,
      });
      setExportResp(res);
      setExportLoading(false);
    },
    [token, parseJson]
  );

  const handleGclSend = useCallback(
    async (body: string) => {
      const parsed = parseJson(body);
      if (!parsed) {
        setGclResp({ status: 0, data: "Invalid JSON", ok: false });
        return;
      }
      setGclLoading(true);
      const res = await api.post(API_PATHS.GCL_CREATE_UPDATE, {
        body: parsed,
        token,
      });
      setGclResp(res);
      setGclLoading(false);
    },
    [token, parseJson]
  );

  const handleShortSend = useCallback(
    async (body: string) => {
      const parsed = parseJson(body);
      if (!parsed) {
        setShortResp({ status: 0, data: "Invalid JSON", ok: false });
        return;
      }
      setShortLoading(true);
      const res = await api.post(API_PATHS.SHORT_CREATE_UPDATE, {
        body: parsed,
        token,
      });
      setShortResp(res);
      setShortLoading(false);
    },
    [token, parseJson]
  );

  const handleCancelSend = useCallback(
    async (body: string) => {
      const parsed = parseJson(body);
      if (!parsed) {
        setCancelResp({ status: 0, data: "Invalid JSON", ok: false });
        return;
      }
      setCancelLoading(true);
      const res = await api.post(API_PATHS.CANCEL_DOCUMENT, {
        body: parsed,
        token,
      });
      setCancelResp(res);
      setCancelLoading(false);
    },
    [token, parseJson]
  );

  const handleXmlSend = useCallback(
    async (body: string) => {
      const parsed = parseJson(body);
      if (!parsed) {
        setXmlResp({ status: 0, data: "Invalid JSON", ok: false });
        return;
      }
      setXmlLoading(true);
      const res = await api.post<Blob | Record<string, unknown>>(API_PATHS.GET_XML, {
        body: parsed,
        token,
      });
      setXmlResp(res);
      setXmlLoading(false);
    },
    [token, parseJson]
  );

  const handleWebhooksSend = useCallback(
    async (body: string) => {
      const trimmed = body.trim();
      let payload: unknown = null;
      if (trimmed) {
        const parsed = parseJson(trimmed);
        if (parsed === null && trimmed !== "null") {
          setWebhooksResp({ status: 0, data: "Invalid JSON", ok: false });
          return;
        }
        payload = parsed ?? null;
      }
      setWebhooksLoading(true);
      const res = await api.post(API_PATHS.WEBHOOKS_SEND_MOCK, {
        body: payload,
        token,
      });
      setWebhooksResp(res);
      setWebhooksLoading(false);
    },
    [token, parseJson]
  );

  const handleLinerSend = useCallback(
    async (body: string) => {
      const parsed = parseJson(body);
      if (!parsed) {
        setLinerResp({ status: 0, data: "Invalid JSON", ok: false });
        return;
      }
      if (!linerApiKey.trim()) {
        setLinerResp({ status: 0, data: "X-API-Key is required for Liner", ok: false });
        return;
      }
      setLinerLoading(true);
      const res = await api.post(API_PATHS.DCSA_BOOKING, {
        body: parsed,
        apiKey: linerApiKey.trim(),
      });
      setLinerResp(res);
      setLinerLoading(false);
    },
    [linerApiKey, parseJson]
  );

  const handleLinerWebhookSend = useCallback(
    async (body: string) => {
      const trimmed = body.trim();
      let payload: unknown = null;
      if (trimmed) {
        const parsed = parseJson(trimmed);
        if (parsed === null && trimmed !== "null") {
          setLinerWebhookResp({ status: 0, data: "Invalid JSON", ok: false });
          return;
        }
        payload = parsed ?? null;
      }
      if (!linerApiKey.trim()) {
        setLinerWebhookResp({
          status: 0,
          data: "X-API-Key is required for Liner webhook send-mock",
          ok: false,
        });
        return;
      }
      setLinerWebhookLoading(true);
      const res = await api.post(API_PATHS.LINER_WEBHOOKS_SEND_MOCK, {
        body: payload,
        apiKey: linerApiKey.trim(),
        headers: linerSkipOutboundClientHeader
          ? undefined
          : { "X-Liner-Webhook-Client-Key": linerOutboundWebhookKey },
      });
      setLinerWebhookResp(res);
      setLinerWebhookLoading(false);
    },
    [
      linerApiKey,
      linerOutboundWebhookKey,
      linerSkipOutboundClientHeader,
      parseJson,
    ]
  );

  const regenerateLinerWebhookIds = useCallback(() => {
    setLinerWHCloudEventId(crypto.randomUUID());
  }, []);

  // Simulation config helpers
  const fetchSimConfig = useCallback(async () => {
    setSimConfigError(null);
    setSimConfigLoading(true);
    const res = await api.get<{ Success: boolean; Config: SimulationConfig }>(
      API_PATHS.SIMULATION_CONFIG
    );
    setSimConfigLoading(false);
    if (!res.ok || !res.data || !(res.data as any).Success) {
      setSimConfigError(
        `Failed to load simulation config: ${JSON.stringify(res.data)}`
      );
      return;
    }
    const raw = (res.data as any).Config as SimulationConfig;
    const normalized = {
      ...raw,
      inboundApiKey: raw.inboundApiKey ?? "",
      outboundWebhookApiKey: raw.outboundWebhookApiKey ?? "",
      linerWebhookUrl: raw.linerWebhookUrl ?? "",
      linerWebhook: { ...DEFAULT_LINER_WEBHOOK, ...raw.linerWebhook },
    };
    setSimConfig(normalized);
    setLinerApiKey(normalized.inboundApiKey);
    setLinerOutboundWebhookKey(normalized.outboundWebhookApiKey);
    setLinerSkipOutboundClientHeader(false);
  }, []);

  useEffect(() => {
    if (
      (tab === "config" || tab === "liner") &&
      !simConfig &&
      !simConfigLoading
    ) {
      void fetchSimConfig();
    }
  }, [tab, simConfig, simConfigLoading, fetchSimConfig]);

  const updateSimFlag = useCallback(
    (key: SimulateErrorApiKey, value: boolean) => {
      setSimConfig((prev) =>
        prev
          ? {
              ...prev,
              simulateError: { ...prev.simulateError, [key]: value },
            }
          : prev
      );
    },
    []
  );

  const updateLinerWebhookFlag = useCallback(
    (key: keyof LinerWebhookSimulationConfig, value: boolean) => {
      setSimConfig((prev) =>
        prev
          ? {
              ...prev,
              linerWebhook: { ...prev.linerWebhook, [key]: value },
            }
          : prev
      );
    },
    []
  );

  const handleSimConfigSave = useCallback(async () => {
    if (!simConfig) return;
    setSimConfigError(null);
    setSimConfigSaving(true);
    const res = await api.put<{ Success: boolean; Config: SimulationConfig; Message?: string[] }>(
      API_PATHS.SIMULATION_CONFIG,
      {
        body: {
          ...simConfig,
          inboundApiKey: linerApiKey,
          outboundWebhookApiKey: linerOutboundWebhookKey,
        },
      }
    );
    setSimConfigSaving(false);
    if (!res.ok || !res.data || !(res.data as any).Success) {
      const msg =
        (res.data as any)?.Message ??
        `Failed to save: ${typeof res.data === "string" ? res.data : JSON.stringify(res.data)}`;
      const msgStr = Array.isArray(msg) ? msg.join(", ") : String(msg);
      setSimConfigError(msgStr);
      toast(msgStr, "error");
      return;
    }
    const saved = (res.data as any).Config as SimulationConfig;
    const normalized = {
      ...saved,
      inboundApiKey: saved.inboundApiKey ?? "",
      outboundWebhookApiKey: saved.outboundWebhookApiKey ?? "",
      linerWebhookUrl: saved.linerWebhookUrl ?? "",
      linerWebhook: { ...DEFAULT_LINER_WEBHOOK, ...saved.linerWebhook },
    };
    setSimConfig(normalized);
    setLinerApiKey(normalized.inboundApiKey);
    setLinerOutboundWebhookKey(normalized.outboundWebhookApiKey);
    setLinerSkipOutboundClientHeader(false);
    toast("Config saved", "success");
  }, [linerApiKey, linerOutboundWebhookKey, simConfig, toast]);

  const refreshLinerSimulationSection = useCallback(async () => {
    await Promise.all([fetchSimConfig(), fetchLinerWebhookDefaults()]);
  }, [fetchSimConfig, fetchLinerWebhookDefaults]);

  const selectedLinerWebhookPreset = getLinerWebhookPreset(linerWebhookPresetId);

  return (
    <div className="flex w-full min-h-0 flex-1 flex-col gap-6 md:flex-row md:items-stretch">
      {/* Sidebar - sticky below header */}
      <div className="scrollbar-custom flex max-h-[calc(100svh-5rem)] w-full shrink-0 flex-col gap-1 overflow-y-auto bg-[var(--sidebar-background)]/95 pr-2 backdrop-blur-sm md:sticky md:top-20 md:w-64 md:self-start md:border-r md:border-[var(--sidebar-border)] md:rounded-xl md:py-3 md:-my-1 md:pr-3 md:shadow-sm">
        <nav aria-label="Workspace modules" className="px-1">
          {NAV_GROUPS.map((group) => (
            <div key={group.title} className="mb-5 last:mb-0">
              <h2 className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground mb-2 px-3">
                {group.title}
              </h2>
              <div className="flex flex-col gap-0.5">
                {group.tabs.map((t) => {
                  const Icon = t.icon;
                  return (
                    <button
                      key={t.id}
                      type="button"
                      onClick={() => setTab(t.id)}
                      aria-current={tab === t.id ? "page" : undefined}
                      className={cn(
                        "flex items-center gap-3 text-sm font-medium px-3 py-2.5 rounded-lg transition-all text-left w-full",
                        tab === t.id
                          ? "bg-[var(--sidebar-accent)] text-[var(--sidebar-accent-foreground)] shadow-sm ring-1 ring-[var(--sidebar-border)]"
                          : "text-[var(--sidebar-foreground)]/80 hover:bg-muted/60 hover:text-foreground"
                      )}
                    >
                      <Icon
                        className={cn(
                          "size-4 shrink-0",
                          tab === t.id ? "text-[var(--sidebar-primary)]" : "opacity-70"
                        )}
                        aria-hidden
                      />
                      {t.label}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </nav>
        
        <div className="mt-8 px-4">
          <h2 className="text-sm font-bold uppercase tracking-wider text-muted-foreground mb-3">Connection</h2>
          <p className="text-sm text-muted-foreground break-all bg-muted/30 p-3 rounded-md border border-white/5">
            <span className="font-semibold block mb-1">Backend URL:</span>
            <code>{API_BASE_URL}</code>
          </p>
          {isAuthenticated && (
            <div className="mt-3 flex items-center gap-2 text-sm font-semibold text-emerald-600 dark:text-emerald-400 bg-emerald-500/10 px-3 py-2 rounded-md border border-emerald-500/20">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
              </span>
              Logged In
            </div>
          )}
        </div>
      </div>

      {/* Main content: fills viewport below header (same flex pattern as Webhooks JSON panel) */}
      <div className="flex min-h-0 min-w-0 flex-1 basis-0 animate-in fade-in slide-in-from-right-4 flex-col duration-500">
      {tab === "login" && (
        <Card className="flex min-h-0 min-w-0 flex-1 flex-col border-white/10 shadow-sm">
          <CardHeader className="shrink-0">
            <CardTitle>Login</CardTitle>
          </CardHeader>
          <CardContent className="min-h-0 flex-1 space-y-4 overflow-y-auto scrollbar-custom">
            {isAuthenticated ? (
              <div>
                <p className="text-muted-foreground">You are logged in.</p>
                <Button variant="outline" onClick={logout} className="mt-2">
                  Logout
                </Button>
              </div>
            ) : (
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  handleLogin();
                }}
                className="space-y-4"
              >
                <div className="space-y-2">
                  <Label htmlFor="username">Username</Label>
                  <Input
                    id="username"
                    value={loginUsername}
                    onChange={(e) => setLoginUsername(e.target.value)}
                    placeholder="admin"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="password">Password</Label>
                  <Input
                    id="password"
                    type="password"
                    value={loginPassword}
                    onChange={(e) => setLoginPassword(e.target.value)}
                    placeholder="admin"
                  />
                </div>
                {loginError && (
                  <div
                    className="rounded-lg border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm font-medium text-destructive"
                    role="alert"
                  >
                    {loginError}
                  </div>
                )}
                <Button type="submit" disabled={loginLoading}>
                  {loginLoading ? "Logging in…" : "Login"}
                </Button>
              </form>
            )}
          </CardContent>
        </Card>
      )}

      {tab === "health" && (
        <Card className="flex min-h-0 min-w-0 flex-1 flex-col border-white/10 shadow-sm">
          <CardHeader className="shrink-0">
            <CardTitle>Health</CardTitle>
          </CardHeader>
          <CardContent className="min-h-0 flex-1 space-y-4 overflow-y-auto scrollbar-custom">
            <Button onClick={handleHealth} disabled={healthLoading}>
              {healthLoading ? "Checking…" : "GET /health"}
            </Button>
            {healthResp && <ResponseBlock {...healthResp} />}
          </CardContent>
        </Card>
      )}

      {tab === "export" && (
        <div className="flex min-h-0 min-w-0 flex-1 flex-col">
          <EndpointBlock
            className="mt-0 flex-1"
            title={`POST ${API_PATHS.EXPORT_DECL}`}
            loading={exportLoading}
            response={exportResp}
            onSend={handleExportSend}
            defaultBody={JSON.stringify(sampleExportDecl, null, 2)}
            listPath={API_PATHS.EXPORT_ENTRIES}
          />
        </div>
      )}

      {tab === "gcl" && (
        <div className="flex min-h-0 min-w-0 flex-1 flex-col">
          <EndpointBlock
            className="mt-0 flex-1"
            title={`POST ${API_PATHS.GCL_CREATE_UPDATE}`}
            loading={gclLoading}
            response={gclResp}
            onSend={handleGclSend}
            defaultBody={JSON.stringify(sampleGCL, null, 2)}
            listPath={API_PATHS.GCL_ENTRIES}
          />
        </div>
      )}

      {tab === "short" && (
        <div className="flex min-h-0 min-w-0 flex-1 flex-col">
          <EndpointBlock
            className="mt-0 flex-1"
            title={`POST ${API_PATHS.SHORT_CREATE_UPDATE}`}
            loading={shortLoading}
            response={shortResp}
            onSend={handleShortSend}
            defaultBody={JSON.stringify(sampleShort, null, 2)}
            listPath={API_PATHS.SHORT_ENTRIES}
          />
        </div>
      )}

      {tab === "cancel" && (
        <div className="flex min-h-0 min-w-0 flex-1 flex-col">
          <EndpointBlock
            className="mt-0 flex-1"
            title={`POST ${API_PATHS.CANCEL_DOCUMENT}`}
            loading={cancelLoading}
            response={cancelResp}
            onSend={handleCancelSend}
            defaultBody={JSON.stringify(sampleCancel, null, 2)}
          />
        </div>
      )}

      {tab === "xml" && (
        <div className="flex min-h-0 min-w-0 flex-1 flex-col">
          <EndpointBlock
            className="mt-0 flex-1"
            title={`POST ${API_PATHS.GET_XML}`}
            loading={xmlLoading}
            response={xmlResp}
            onSend={handleXmlSend}
            defaultBody={JSON.stringify(sampleGetXml, null, 2)}
          />
        </div>
      )}

      {tab === "webhooks" && (
        <div className="flex min-h-0 min-w-0 flex-1 flex-col gap-4">
          <p className="shrink-0 text-sm text-muted-foreground">
            Sends the JSON body to the client via POST {`${API_BASE_URL}${API_PATHS.WEBHOOKS_SEND_MOCK}`}. The backend forwards the payload to the configured client webhook URL (CLIENT_WEBHOOK_URL). Use InterfaceId, MessageType, OMSCustomsId, ReferenceNumber, Status (PascalCase) for TIFFA format.
          </p>
          <Card className="shrink-0 border-white/10 shadow-sm">
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Select message to simulate (Update Status)</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="grid gap-2 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label className="text-muted-foreground">Document</Label>
                  <Select
                    value={selectedDocument}
                    onValueChange={(v) => onDocumentChange(v as Document)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {DOCUMENTS.map((doc) => (
                        <SelectItem key={doc} value={doc}>
                          {doc}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-muted-foreground">Message</Label>
                  <Select
                    value={selectedMessageId ?? ""}
                    onValueChange={(v) => setSelectedMessageId(v || null)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select message" />
                    </SelectTrigger>
                    <SelectContent>
                      {webhookEntries.map((entry) => (
                        <SelectItem key={entry.id} value={entry.id}>
                          {entry.messageType} {entry.statusCode} – {entry.description}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="shrink-0 border-white/10 shadow-sm">
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Quick edit</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="grid gap-2 sm:grid-cols-3">
                <div className="space-y-2">
                  <Label className="text-muted-foreground">InterfaceId</Label>
                  <Input
                    value={webhookInterfaceId}
                    onChange={(e) => injectInterfaceId(e.target.value)}
                    placeholder="e.g. UUID"
                    className="font-mono text-sm"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-muted-foreground">ReferenceNumber</Label>
                  <Input
                    value={webhookReferenceNumber}
                    onChange={(e) => injectReferenceNumber(e.target.value)}
                    placeholder="Reference number"
                    className="font-mono text-sm"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-muted-foreground">OMSCustomsId</Label>
                  <Input
                    value={webhookOMSCustomsId}
                    onChange={(e) => injectOMSCustomsId(e.target.value)}
                    placeholder="Leave empty for null"
                    className="font-mono text-sm"
                  />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="flex min-h-0 min-w-0 flex-1 flex-col border-white/10 shadow-sm">
            <CardHeader className="shrink-0 pb-2">
              <CardTitle className="text-base">POST {API_PATHS.WEBHOOKS_SEND_MOCK}</CardTitle>
            </CardHeader>
            <CardContent className="flex min-h-0 flex-1 flex-col space-y-2 overflow-hidden">
              <div className="flex min-h-0 flex-1 flex-col space-y-1.5">
                <Label className="shrink-0 text-muted-foreground">Request body (JSON)</Label>
                <textarea
                  value={webhookBody}
                  onChange={(e) => {
                    setWebhookBody(e.target.value);
                    if (webhookJsonError) setWebhookJsonError(null);
                  }}
                  onBlur={() => {
                    const trimmed = webhookBody.trim();
                    if (trimmed !== "" && trimmed !== "null" && !validateJsonBody(webhookBody)) {
                      setWebhookJsonError("Invalid JSON");
                    } else {
                      setWebhookJsonError(null);
                    }
                    const parsed = parseJson(webhookBody) as Record<string, unknown> | null;
                    if (parsed && typeof parsed === "object") {
                      if (typeof parsed.InterfaceId === "string") setWebhookInterfaceId(parsed.InterfaceId);
                      if (typeof parsed.ReferenceNumber === "string") setWebhookReferenceNumber(parsed.ReferenceNumber);
                      setWebhookOMSCustomsId(parsed.OMSCustomsId != null ? String(parsed.OMSCustomsId) : "");
                    }
                  }}
                  className={cn(
                    "mt-1 min-h-[200px] w-full flex-1 resize-y rounded-md border border-input bg-background px-3 py-2 font-mono text-sm scrollbar-custom sm:min-h-[280px] lg:min-h-[320px]",
                    webhookJsonError && "border-destructive/50"
                  )}
                  spellCheck={false}
                />
                {webhookJsonError && (
                  <p className="shrink-0 text-sm font-medium text-destructive" role="alert">
                    {webhookJsonError}
                  </p>
                )}
              </div>
              <Button
                onClick={() => {
                  if (
                    webhookBody.trim() !== "" &&
                    webhookBody.trim() !== "null" &&
                    !validateJsonBody(webhookBody)
                  ) {
                    setWebhookJsonError("Invalid JSON");
                    return;
                  }
                  setWebhookJsonError(null);
                  handleWebhooksSend(webhookBody);
                }}
                disabled={webhooksLoading}
              >
                {webhooksLoading ? "Sending…" : "Send"}
              </Button>
              <p className="shrink-0 text-sm text-muted-foreground">
                POST to {API_BASE_URL}{API_PATHS.WEBHOOKS_SEND_MOCK} with Content-Type: application/json. Empty body sends <code>null</code>.
              </p>
              {webhooksResp && (
                <div className="max-h-[min(40vh,24rem)] shrink-0 overflow-y-auto scrollbar-custom">
                  <ResponseBlock {...webhooksResp} />
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {tab === "liner" && (
        <div className="flex min-h-0 min-w-0 flex-1 flex-col gap-4 overflow-y-auto scrollbar-custom">
          <p className="shrink-0 text-sm text-muted-foreground">
            Liner webhook (mock) POSTs JSON to {API_BASE_URL}
            {API_PATHS.LINER_WEBHOOKS_SEND_MOCK}. The server forwards the body to{" "}
            <code className="text-xs">LINER_WEBHOOK_URL</code> (e.g. EMP{" "}
            <code className="text-xs">/api/v2/webhooks/liner</code>). The outbound{" "}
            <code className="text-xs">X-API-Key</code> defaults from the saved simulation config and can
            be overridden below for one send. The mock{" "}
            <code className="text-xs">X-API-Key</code> authenticates send-mock and DCSA booking. For a{" "}
            <strong>full field-by-field</strong> builder aligned with EMP liner domain types, open{" "}
            <a
              href="#/liner-webhook-mock"
              className="font-medium text-primary underline-offset-4 hover:underline"
            >
              Liner webhook mock
            </a>{" "}
            (<code className="text-xs">#/liner-webhook-mock</code>).
          </p>
          <Card className="shrink-0 border-white/10 shadow-sm">
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Stored liner endpoint</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-xs text-muted-foreground">
                Saved in <code className="text-[11px]">simulation_config</code>. Update it here and use{" "}
                <strong>Save simulation settings</strong> below.
              </p>
              <div>
                <Label className="text-muted-foreground">LINER_WEBHOOK_URL</Label>
                <Input
                  value={simConfig?.linerWebhookUrl ?? ""}
                  onChange={(e) =>
                    setSimConfig((prev) =>
                      prev ? { ...prev, linerWebhookUrl: e.target.value } : prev
                    )
                  }
                  placeholder="Not set — auto outbound webhooks and send-mock target disabled"
                  className="mt-1 font-mono text-sm"
                />
              </div>
            </CardContent>
          </Card>
          <Card className="shrink-0 border-white/10 shadow-sm">
            <CardHeader className="pb-2">
              <CardTitle className="text-base">DCSA — auto webhooks</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-xs text-muted-foreground">
                Stored in <code className="text-[11px]">simulation_config</code>. Applies when{" "}
                <code className="text-[11px]">LINER_WEBHOOK_URL</code> is set.{" "}
                <strong>POST/PUT</strong> <code className="text-[11px]">/api/dcsa-booking</code> always
                send a delayed confirmation unless you enable random rejection. Amend and cancel are
                opt-in.
              </p>
              {simConfigLoading && !simConfig && (
                <p className="text-sm text-muted-foreground">Loading config…</p>
              )}
              {simConfig && (
                <div className="grid max-w-xl gap-3">
                  <label className="flex items-center gap-2 rounded-md border border-input px-3 py-2 text-sm">
                    <input
                      type="checkbox"
                      className="h-4 w-4 rounded"
                      checked={simConfig.linerWebhook.autoAfterAmend}
                      onChange={(e) =>
                        updateLinerWebhookFlag("autoAfterAmend", e.target.checked)
                      }
                    />
                    <span>
                      Auto webhook after <strong>amend</strong> (
                      <code className="text-xs">PUT /api/dcsa-booking/:lsa_booking_ref</code>)
                    </span>
                  </label>
                  <label className="flex items-center gap-2 rounded-md border border-input px-3 py-2 text-sm">
                    <input
                      type="checkbox"
                      className="h-4 w-4 rounded"
                      checked={simConfig.linerWebhook.autoAfterCancel}
                      onChange={(e) =>
                        updateLinerWebhookFlag("autoAfterCancel", e.target.checked)
                      }
                    />
                    <span>
                      Auto Maersk-style{" "}
                      <code className="text-xs">org.dcsa.booking.v2</code> (cancellation accepted,{" "}
                      <code className="text-xs">bookingStatus: CANCELLED</code>) after{" "}
                      <strong>cancel</strong> (<code className="text-xs">DELETE</code>)
                    </span>
                  </label>
                  <label className="flex items-center gap-2 rounded-md border border-input px-3 py-2 text-sm">
                    <input
                      type="checkbox"
                      className="h-4 w-4 rounded"
                      checked={simConfig.linerWebhook.randomRejection}
                      onChange={(e) =>
                        updateLinerWebhookFlag("randomRejection", e.target.checked)
                      }
                    />
                    <span>
                      Random <code className="text-xs">booking_rejection</code> (~10%) on
                      create/update/amend webhooks
                    </span>
                  </label>
                </div>
              )}
            </CardContent>
          </Card>
          <Card className="shrink-0 border-white/10 shadow-sm">
            <CardHeader className="pb-2">
              <CardTitle className="text-base">DCSA — simulate API errors</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-xs text-muted-foreground">
                When enabled, the mock returns HTTP 500 for that DCSA operation (same flags as{" "}
                <strong>Config</strong> tab). Use <strong>Save simulation settings</strong> below.
              </p>
              {simConfig && (
                <div className="grid max-w-xl gap-3 sm:grid-cols-2">
                  {DCSA_SIMULATE_ERROR_KEYS.map((k) => (
                    <label
                      key={k}
                      className="flex items-center gap-2 rounded-md border border-input px-3 py-2 text-sm"
                    >
                      <input
                        type="checkbox"
                        className="h-4 w-4 rounded"
                        checked={!!simConfig.simulateError[k]}
                        onChange={(e) => updateSimFlag(k, e.target.checked)}
                      />
                      <span className="font-mono text-xs" title={`SIMULATE_ERROR_${k}`}>
                        SIMULATE_ERROR_{k}
                      </span>
                    </label>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
          {simConfigError && (
            <div
              className="shrink-0 rounded-lg border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm font-medium text-destructive"
              role="alert"
            >
              {simConfigError}
            </div>
          )}
          <div className="flex shrink-0 flex-wrap items-center gap-2">
            <Button
              type="button"
              onClick={handleSimConfigSave}
              disabled={!simConfig || simConfigSaving}
            >
              {simConfigSaving ? "Saving…" : "Save simulation settings"}
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => void refreshLinerSimulationSection()}
              disabled={simConfigLoading}
            >
              {simConfigLoading ? "Refreshing…" : "Refresh from server"}
            </Button>
          </div>
          <p className="shrink-0 text-xs text-muted-foreground">
            Save persists the full <code className="text-[11px]">simulation_config</code> row (including
            slow-speed, liner endpoint, and non-DCSA error flags from the <strong>Config</strong> tab).
            Refresh reloads the current database values.
          </p>
          <Card className="shrink-0 border-white/10 shadow-sm">
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Liner webhook — preset &amp; quick fields</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <Label className="text-muted-foreground">
                  X-API-Key (mock Liner / DCSA)
                </Label>
                <Input
                  value={linerApiKey}
                  onChange={(e) => setLinerApiKey(e.target.value)}
                  placeholder="Saved inbound API key"
                  className="mt-1 font-mono"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Saved to <code className="text-[11px]">simulation_config.inbound_api_key</code> when
                  you click <strong>Save simulation settings</strong>.
                </p>
              </div>
              <div>
                <Label className="text-muted-foreground">
                  Client webhook X-API-Key (outbound)
                </Label>
                <Input
                  value={linerOutboundWebhookKey}
                  onChange={(e) => setLinerOutboundWebhookKey(e.target.value)}
                  placeholder="Saved outbound webhook API key"
                  className="mt-1 font-mono"
                  disabled={linerSkipOutboundClientHeader}
                />
                <p className="text-xs text-muted-foreground mt-1">
                  {linerSkipOutboundClientHeader
                    ? "Loading saved config… outbound key stays disabled until load completes."
                    : "Override for this send only; clear to omit X-API-Key on the client POST."}
                </p>
              </div>
              <div className="space-y-2">
                <Label className="text-muted-foreground">Preset</Label>
                <Select
                  value={linerWebhookPresetId}
                  onValueChange={(v) => setLinerWebhookPresetId(v)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select preset" />
                  </SelectTrigger>
                  <SelectContent>
                    {LINER_WEBHOOK_PRESETS.map((p) => (
                      <SelectItem key={p.id} value={p.id}>
                        {p.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {selectedLinerWebhookPreset?.description && (
                  <p className="text-xs text-muted-foreground">
                    {selectedLinerWebhookPreset.description}
                  </p>
                )}
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={regenerateLinerWebhookIds}
                  title="New random UUID for CloudEvents id"
                >
                  Generate CloudEvents id
                </Button>
              </div>
              <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                <div className="space-y-2">
                  <Label className="text-muted-foreground">lsaBookingRef</Label>
                  <Input
                    value={linerWHLsaRef}
                    onChange={(e) => setLinerWHLsaRef(e.target.value)}
                    className="font-mono text-sm"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-muted-foreground">linerCode</Label>
                  <Input
                    value={linerWHLinerCode}
                    onChange={(e) => setLinerWHLinerCode(e.target.value)}
                    className="font-mono text-sm"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-muted-foreground">CloudEvents id</Label>
                  <Input
                    value={linerWHCloudEventId}
                    onChange={(e) => setLinerWHCloudEventId(e.target.value)}
                    className="font-mono text-sm"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-muted-foreground">subject (CE fallback)</Label>
                  <Input
                    value={linerWHSubject}
                    onChange={(e) => setLinerWHSubject(e.target.value)}
                    className="font-mono text-sm"
                  />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="flex min-h-[min(24rem,50svh)] min-w-0 shrink-0 flex-col border-white/10 shadow-sm md:min-h-[min(28rem,45svh)]">
            <CardHeader className="shrink-0 pb-2">
              <CardTitle className="text-base">POST {API_PATHS.LINER_WEBHOOKS_SEND_MOCK}</CardTitle>
            </CardHeader>
            <CardContent className="flex min-h-0 flex-1 flex-col space-y-2 overflow-hidden">
              <div className="flex min-h-0 flex-1 flex-col space-y-1.5">
                <Label className="shrink-0 text-muted-foreground">Request body (JSON)</Label>
                <textarea
                  value={linerWebhookBody}
                  onChange={(e) => {
                    setLinerWebhookBody(e.target.value);
                    if (linerWebhookJsonError) setLinerWebhookJsonError(null);
                  }}
                  onBlur={() => {
                    const trimmed = linerWebhookBody.trim();
                    if (trimmed !== "" && trimmed !== "null" && !validateJsonBody(linerWebhookBody)) {
                      setLinerWebhookJsonError("Invalid JSON");
                    } else {
                      setLinerWebhookJsonError(null);
                    }
                  }}
                  className={cn(
                    "mt-1 min-h-[200px] w-full flex-1 resize-y rounded-md border border-input bg-background px-3 py-2 font-mono text-sm scrollbar-custom sm:min-h-[280px] lg:min-h-[320px]",
                    linerWebhookJsonError && "border-destructive/50"
                  )}
                  spellCheck={false}
                />
                {linerWebhookJsonError && (
                  <p className="shrink-0 text-sm font-medium text-destructive" role="alert">
                    {linerWebhookJsonError}
                  </p>
                )}
              </div>
              <Button
                type="button"
                onClick={() => {
                  if (
                    linerWebhookBody.trim() !== "" &&
                    linerWebhookBody.trim() !== "null" &&
                    !validateJsonBody(linerWebhookBody)
                  ) {
                    setLinerWebhookJsonError("Invalid JSON");
                    return;
                  }
                  setLinerWebhookJsonError(null);
                  handleLinerWebhookSend(linerWebhookBody);
                }}
                disabled={linerWebhookLoading}
              >
                {linerWebhookLoading ? "Sending…" : "Send Liner webhook"}
              </Button>
              {linerWebhookResp && (
                <div className="max-h-[min(40vh,24rem)] shrink-0 overflow-y-auto scrollbar-custom">
                  <ResponseBlock {...linerWebhookResp} />
                </div>
              )}
            </CardContent>
          </Card>
          <EndpointBlock
            className="mt-0 shrink-0"
            title={`POST ${API_PATHS.DCSA_BOOKING}`}
            loading={linerLoading}
            response={linerResp}
            onSend={handleLinerSend}
            defaultBody={JSON.stringify(sampleDcsaBooking, null, 2)}
            tokenRequired={false}
          />
        </div>
      )}

      {tab === "config" && (
        <Card className="flex min-h-0 min-w-0 flex-1 flex-col border-white/10 shadow-sm">
          <CardHeader className="shrink-0">
            <CardTitle>Simulation config</CardTitle>
          </CardHeader>
          <CardContent className="min-h-0 flex-1 space-y-4 overflow-y-auto scrollbar-custom">
            <p className="text-sm text-muted-foreground">
              Backed by the <code>simulation_config</code> table in the mock server. Changes
              apply immediately without restart.
            </p>
            <div className="space-y-2 max-w-xs">
              <Label>ENABLE_SIMULATE_REAL_SLOW_SPEED (ms)</Label>
              <Input
                type="number"
                min={0}
                value={simConfig?.simulateRealSlowSpeedMs ?? ""}
                onChange={(e) =>
                  setSimConfig((prev) =>
                    prev
                      ? {
                          ...prev,
                          simulateRealSlowSpeedMs: Math.max(
                            0,
                            Number.isNaN(Number(e.target.value))
                              ? 0
                              : Number(e.target.value)
                          ),
                        }
                      : prev
                  )
                }
              />
              <p className="text-xs text-muted-foreground">
                Delay applied to every request when &gt; 0 (e.g. 1000 = 1s).
              </p>
            </div>
            <fieldset className="space-y-3 rounded-lg border border-border px-4 py-3">
              <legend className="text-sm font-medium text-foreground px-1">
                Simulate errors
              </legend>
              <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
                {simConfig &&
                  SIMULATE_ERROR_KEYS_ORDER.map((k) => (
                    <label
                      key={k}
                      className="flex items-center gap-2 rounded border border-input px-3 py-2 text-sm focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2"
                    >
                      <input
                        type="checkbox"
                        className="h-4 w-4 rounded"
                        checked={!!simConfig.simulateError[k]}
                        onChange={(e) => updateSimFlag(k, e.target.checked)}
                      />
                      <span className="font-mono text-xs" title={`SIMULATE_ERROR_${k}`}>
                        SIMULATE_ERROR_{k}
                      </span>
                    </label>
                  ))}
              </div>
            </fieldset>
            {simConfigError && (
              <div
                className="rounded-lg border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm font-medium text-destructive"
                role="alert"
              >
                {simConfigError}
              </div>
            )}
            <div className="flex items-center gap-2">
              <Button
                onClick={handleSimConfigSave}
                disabled={!simConfig || simConfigSaving}
              >
                {simConfigSaving ? "Saving…" : "Save config"}
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => void fetchSimConfig()}
                disabled={simConfigLoading}
              >
                {simConfigLoading ? "Refreshing…" : "Refresh"}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
      </div>
    </div>
  );
}
